import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { url } from "../../constants/url";
import { useAuth } from "../../context/authContext";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const RAZORPAY_KEY_ID =
  process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_live_TTt6Om8EcjjDvw";

const THEMES = {
  NORMAL: {
    accent: "#421A6E",
    light: "#f5f0fc",
    border: "#d4b8f0",
    btnHover: "#2d1050",
    label: "Normal",
    icon: "🏙️",
    sectionTitle: "Normal Plans",
    sectionSub: "Credits for site visits within the city",
  },
  OUTSTATION: {
    accent: "#1a5c9a",
    light: "#eef4fd",
    border: "#a8c8f0",
    btnHover: "#124678",
    label: "Outstation",
    icon: "✈️",
    sectionTitle: "Outstation Plans",
    sectionSub: "Credits for site visits outside the city — credits never expire",
  },
};

// ── Stat Row ──────────────────────────────────────────────────────────────────
const StatRow = ({ label, value, accent, isLast }) => (
  <>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 0.9,
      }}
    >
      <Typography
        sx={{ fontSize: "0.78rem", color: "#888", fontWeight: 500 }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: "0.78rem", color: accent, fontWeight: 700 }}
      >
        {value}
      </Typography>
    </Box>
    {!isLast && <Divider sx={{ borderColor: "#e8e0f5" }} />}
  </>
);

// ── Plan Card ─────────────────────────────────────────────────────────────────
const PlanCard = ({ plan, onBuy, loading }) => {
  const type = plan.planType === "OUTSTATION" ? "OUTSTATION" : "NORMAL";
  const { accent, light, border, btnHover, label } = THEMES[type];

  const basePrice = plan.amount || 0;
  const gstAmount = Math.round(basePrice * 0.18);
  const totalAmount = basePrice + gstAmount;

  const validity =
    type === "OUTSTATION"
      ? "No expiry"
      : `${plan.months || 1} month${(plan.months || 1) > 1 ? "s" : ""}`;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "14px",
        border: `1.5px solid ${border}`,
        width: "100%",          // fills the Grid cell
        height: 320,            // ← FIXED height — every card identical
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: "transform 0.2s, box-shadow 0.2s, border-color 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 10px 28px ${accent}28`,
          borderColor: accent,
        },
      }}
    >
      {/* Accent strip — fixed 4px */}
      <Box sx={{ height: 4, background: accent, flexShrink: 0 }} />

      {/* Body */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          p: 2.5,
          overflow: "hidden",
        }}
      >
        {/* ── Name + badge — fixed 48px zone ── */}
        <Box
          sx={{
            height: 48,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            mb: 1.5,
            overflow: "hidden",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.92rem",
              color: accent,
              lineHeight: 1.35,
              flex: 1,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {plan.planName}
          </Typography>
          {type !== "NORMAL" && (
            <Chip
              label={label}
              size="small"
              sx={{
                background: light,
                color: accent,
                border: `1px solid ${border}`,
                fontWeight: 700,
                fontSize: "0.6rem",
                height: 20,
                flexShrink: 0,
                mt: 0.2,
              }}
            />
          )}
        </Box>

        {/* ── Amount — fixed 52px zone ── */}
        <Box sx={{ height: 52, display: "flex", alignItems: "center", gap: 0.3, mb: 1.5, position: 'relative' }}>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#aaa", mt: 1 }}>
            ₹
          </Typography>
          <Typography
            sx={{ fontSize: "1.8rem", fontWeight: 800, color: "#1a1a2e", lineHeight: 1 }}
          >
            {plan.amount != null ? basePrice.toLocaleString("en-IN") : "—"}
          </Typography>
          <Box sx={{ ml: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: "#aaa" }}>
              + 18% GST
            </Typography>
            <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: accent }}>
              Total: ₹{plan.amount != null ? totalAmount.toLocaleString("en-IN") : ""}
            </Typography>
          </Box>
        </Box>

        {/* ── Stats — fixed 108px zone ── */}
        <Box
          sx={{
            height: 108,
            background: light,
            borderRadius: "10px",
            px: 1.5,
            flexShrink: 0,
            mb: 1.5,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 0,
          }}
        >
          <StatRow label="Credits" value={plan.credits ?? "—"} accent={accent} />
          <Divider sx={{ borderColor: "#e8e0f5" }} />
          <StatRow label="Validity" value={validity} accent={accent} isLast />
        </Box>
        {/* ── Button — fills remaining space ── */}
        <Box sx={{ flex: 1, display: "flex", alignItems: "flex-end" }}>
          <Button
            variant="contained"
            fullWidth
            disabled={loading === plan._id}
            onClick={() => onBuy(plan)}
            sx={{
              backgroundColor: accent,
              fontWeight: 700,
              borderRadius: "9px",
              py: 1.1,
              fontSize: "0.88rem",
              textTransform: "none",
              boxShadow: `0 4px 12px ${accent}40`,
              "&:hover": { backgroundColor: btnHover },
              "&:disabled": { backgroundColor: "#e0e0e0", boxShadow: "none" },
            }}
          >
            {loading === plan._id ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : (
              "Buy Now"
            )}
          </Button>
        </Box>
      </Box>
    </Card>
  );
};

// ── Section ───────────────────────────────────────────────────────────────────
const Section = ({ type, plans, onBuy, loading }) => {
  const { accent, light, border, icon, sectionTitle, sectionSub } = THEMES[type];

  return (
    <Box mb={5}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: light,
            border: `1.5px solid ${border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.1rem",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: accent, lineHeight: 1.2 }}>
            {sectionTitle}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#888" }}>
            {sectionSub}
          </Typography>
        </Box>
      </Box>

      {/* Cards or empty */}
      {plans.length === 0 ? (
        <Box
          sx={{
            py: 4,
            textAlign: "center",
            background: light,
            borderRadius: "12px",
            border: `1.5px dashed ${border}`,
          }}
        >
          <Typography sx={{ color: "#999", fontSize: "0.85rem" }}>
            No plans available in this category.
          </Typography>
        </Box>
      ) : (
        // Strict 3-column grid
        <Box sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(3, 1fr)",
            xl: "repeat(3, 1fr)"
          },
          gap: 3,
          width: "100%"
        }}>
          {plans.map((plan) => (
            <Box
              key={plan._id}
              sx={{ display: "flex", width: "100%" }}
            >
              <PlanCard plan={plan} onBuy={onBuy} loading={loading} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const BuyCredits = () => {
  const { userData, token, updateUserData, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [buyingPlanId, setBuyingPlanId] = useState(null);
  const [successPlan, setSuccessPlan] = useState(null);

  const normalPlans = plans;

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setFetchLoading(true);
        const res = await axios.get(`${url}/api/credits/normal`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setPlans(res.data?.plans || []);
      } catch {
        setFetchError("Failed to load plans. Please try again.");
      } finally {
        setFetchLoading(false);
      }
    };
    fetchPlans();
  }, [token]);

  useEffect(() => {
    if (successPlan) {
      const timer = setTimeout(() => {
        setSuccessPlan(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successPlan]);

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (document.getElementById("razorpay-script")) return resolve(true);
      const script = document.createElement("script");
      script.id = "razorpay-script";
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const handleBuy = async (plan) => {
    if (!userData?._id) return toast.error("Please log in to purchase a plan.");
    setBuyingPlanId(plan._id);

    const basePrice = plan.amount || 0;
    const gstAmount = Math.round(basePrice * 0.18);
    const totalAmount = basePrice + gstAmount;

    try {
      const orderRes = await axios.post(
        `${url}/api/payment/create-order`,
        { amount: totalAmount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const order = orderRes.data?.data;
      if (!order?.id) {
        toast.error("Failed to create payment order.");
        return setBuyingPlanId(null);
      }



      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Could not load Razorpay. Check your internet connection.");
        return setBuyingPlanId(null);
      }

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: "Arconest",
        description: `Purchase: ${plan.planName}`,
        order_id: order.id,
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(
              `${url}/api/payment/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (verifyRes.data?.success) {
              const purchaseRes = await axios.post(
                `${url}/api/credits/purchase`,
                { userID: userData._id, planID: plan._id, razorpayPaymentId: response.razorpay_payment_id },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              if (purchaseRes.data?.credits) {
                updateUserData({ credits: purchaseRes.data.credits });
              }
              if (refreshUserData) {
                refreshUserData();
              }
              window.dispatchEvent(new Event("creditsUpdated"));
              localStorage.setItem("purchase_success", JSON.stringify({
                planName: plan.planName,
                credits: plan.credits,
                clientEmail: purchaseRes.data?.clientEmail || userData?.email,
                purchaseId: purchaseRes.data?.purchaseId,
                timestamp: Date.now()
              }));
              navigate("/projects/add-site-check");
            } else {
              toast.error("Payment verification failed. Contact support.");
            }
          } catch {
            toast.error("Credit provisioning failed. Contact support.");
          } finally {
            setBuyingPlanId(null);
          }
        },
        prefill: {
          name: userData?.name || "",
          email: userData?.email || "",
          contact: userData?.phone || "",
        },
        theme: { color: "#421A6E" },
        modal: {
          ondismiss: () => {
            setBuyingPlanId(null);
            toast("Payment cancelled.", { icon: "ℹ️" });
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch {
      toast.error("Something went wrong. Please try again.");
      setBuyingPlanId(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1100, mx: "auto" }}>
      {/* Page header */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #421A6E, #652D90)",
          borderRadius: "16px",
          p: { xs: 2.5, md: 3.5 },
          mb: 4,
          color: "#fff",
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: "1.4rem", mb: 0.5 }}>
          💳 Buy Credits
        </Typography>
        <Typography sx={{ opacity: 0.82, fontSize: "0.88rem", maxWidth: 520 }}>
          Purchase a credit plan to request field inspection services. Credits are added to your
          active plan balance instantly after payment.
        </Typography>
      </Box>



      {fetchLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress sx={{ color: "#421A6E" }} />
        </Box>
      )}

      {fetchError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {fetchError}
        </Alert>
      )}

      {!fetchLoading && !fetchError && plans.length === 0 && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No credit plans are available right now.
        </Alert>
      )}

      {!fetchLoading && !fetchError && plans.length > 0 && (
        <>
          <Section type="NORMAL" plans={normalPlans} onBuy={handleBuy} loading={buyingPlanId} />
        </>
      )}

    </Box>
  );
};

export default BuyCredits;
